import { useCallback, useEffect, useState } from 'react';
import { T } from '@design/index';
import { GOV_TYPE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientApi, type CarePlan } from '../api';
import type { MedicineReminder } from '../types';

/**
 * Care plans — a goal the patient is working towards and the routine that
 * goes with it.
 *
 * `/care-plans` has been serving these all along with nothing on the web
 * reading or writing them, so a plan agreed with a doctor or set on another
 * device was invisible here. This account has two, including a DASH-diet plan
 * for blood pressure that runs to December.
 *
 * Nothing here is generated by the app: a plan is what the patient (or their
 * clinician) wrote down, and it says so.
 */

const GOALS: { icon: string; label: string; summary: string }[] = [
  { icon: '❤️', label: 'Bring blood pressure down', summary: 'Less salt, a daily walk, and a weekly weigh-in.' },
  { icon: '⚖️', label: 'Reach a healthier weight', summary: 'Smaller portions, no sugary drinks, movement most days.' },
  { icon: '🩸', label: 'Keep blood sugar steady', summary: 'Regular meals, fewer refined carbohydrates, a walk after dinner.' },
  { icon: '🏃', label: 'Get moving regularly', summary: 'Thirty minutes of activity, five days a week.' },
  { icon: '😴', label: 'Sleep better', summary: 'A fixed bedtime, no screens for the last hour.' },
  { icon: '🚭', label: 'Stop smoking', summary: 'A quit date, and a plan for the moments that trigger it.' },
];

const card: React.CSSProperties = {
  background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
  padding: 16, boxShadow: T.shadow, fontFamily: T.font,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: `1px solid ${T.border}`,
  borderRadius: 10, fontSize: 13, fontFamily: T.font, color: T.text,
  outline: 'none', boxSizing: 'border-box',
};

function fmtDate(v?: string | null): string {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function CarePlans() {
  const [plans, setPlans] = useState<CarePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');
  const [adding, setAdding] = useState(false);
  const [items, setItems] = useState<MedicineReminder[]>([]);

  const [goal, setGoal] = useState(GOALS[0]);
  const [summary, setSummary] = useState(GOALS[0].summary);
  const [endDate, setEndDate] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setPlans(await patientApi.carePlans.list() ?? []);
    } catch (e: any) {
      setError(e?.body?.message || 'Your care plans could not be loaded — check your connection.');
    } finally {
      setLoading(false);
    }
    // A plan's items ARE reminders, tied to it by `groupId`. The page used to
    // show only the goal, so what the plan actually asks of the patient — nine
    // items on one of this account's plans — appeared nowhere.
    try {
      setItems(await patientApi.getMedicines());
    } catch {
      // Supporting detail: a plan still reads without its items.
      setItems([]);
    }
  }, []);

  /** This plan's items, in the order the day runs. */
  const itemsFor = (p: CarePlan) =>
    items
      .filter((m) => m.groupId === p.groupId)
      .sort((a, b) => (a.times?.[0] ?? '').localeCompare(b.times?.[0] ?? ''));

  useEffect(() => { void load(); }, [load]);

  const add = async () => {
    setError(''); setNotice('');
    if (summary.trim().length < 5) { setError('Write what the plan actually asks of you.'); return; }
    setBusy('new');
    try {
      await patientApi.carePlans.create({
        // The id is the patient's own handle on this plan; the server keys by it.
        groupId: `plan-${Date.now().toString(36)}`,
        goalLabel: goal.label,
        goalIcon: goal.icon,
        summary: summary.trim(),
        ...(endDate ? { endDate } : {}),
      });
      setAdding(false); setEndDate('');
      setNotice('Plan saved.');
      await load();
    } catch (e: any) {
      setError(e?.body?.errors?.[0]?.message || e?.body?.message || 'That plan could not be saved.');
    } finally {
      setBusy('');
    }
  };

  const remove = async (p: CarePlan) => {
    // Removing a plan ENDS every reminder it created. That is a whole daily
    // routine disappearing, so it is said before it happens, not after.
    const n = itemsFor(p).length;
    const warning = n > 0
      ? `Remove "${p.goalLabel}"? Its ${n} tracked item${n === 1 ? '' : 's'} will be ended too. Doses you have already logged stay on your record.`
      : `Remove "${p.goalLabel}"?`;
    if (!window.confirm(warning)) return;

    setBusy(p.groupId); setError(''); setNotice('');
    try {
      const res = await patientApi.carePlans.remove(p.groupId);
      setPlans((rows) => rows.filter((r) => r.groupId !== p.groupId));
      setItems((rows) => rows.filter((m) => m.groupId !== p.groupId));
      const ended = res?.itemsEnded ?? 0;
      setNotice(ended > 0
        ? `Plan removed — ${ended} tracked item${ended === 1 ? '' : 's'} ended.`
        : 'Plan removed.');
    } catch (e: any) {
      setError(e?.body?.message || 'That plan could not be removed.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div style={{ ...govPageContainer(), display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div>
          <h1 style={{ ...GOV_TYPE.pageTitle }}>Care plans</h1>
          <p style={{ fontSize: 11.5, color: T.textSec, marginTop: 4, fontFamily: T.font, lineHeight: 1.5 }}>
            What you are working towards, and the routine that goes with it.
          </p>
        </div>
        <button
          onClick={() => { setAdding((v) => !v); setError(''); setNotice(''); }}
          style={{
            padding: '10px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
            fontSize: 12, fontWeight: 700, color: T.white, background: T.teal,
            fontFamily: T.font, whiteSpace: 'nowrap',
          }}
        >
          {adding ? 'Close' : 'New plan'}
        </button>
      </div>

      {error && <div style={{ ...card, borderColor: `${T.danger}55`, padding: 12, fontSize: 12, color: T.danger }}>{error}</div>}
      {notice && <div style={{ ...card, borderColor: `${T.teal}55`, padding: 12, fontSize: 12, color: T.teal }}>{notice}</div>}

      {adding && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 800, color: T.text, marginBottom: 10 }}>What are you working on?</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {GOALS.map((g) => (
              <button
                key={g.label}
                onClick={() => { setGoal(g); setSummary(g.summary); }}
                style={{
                  padding: '8px 13px', borderRadius: 20, cursor: 'pointer',
                  fontSize: 11, fontWeight: 700, fontFamily: T.font,
                  color: goal.label === g.label ? T.white : T.textSec,
                  background: goal.label === g.label ? T.teal : T.white,
                  border: `1px solid ${goal.label === g.label ? T.teal : T.border}`,
                }}
              >
                {g.icon} {g.label}
              </button>
            ))}
          </div>

          <label htmlFor="plan-summary" style={{ fontSize: 11, fontWeight: 600, color: T.textSec, display: 'block', marginBottom: 6 }}>
            The plan, in your own words
          </label>
          <textarea
            id="plan-summary"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={3}
            style={{ ...inputStyle, resize: 'vertical' }}
          />

          <label htmlFor="plan-end" style={{ fontSize: 11, fontWeight: 600, color: T.textSec, display: 'block', margin: '12px 0 6px' }}>
            Review it by (optional)
          </label>
          <input id="plan-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={inputStyle} />

          <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 10, lineHeight: 1.5 }}>
            This is your own note, not medical advice. Anything a doctor has told you to do takes precedence.
          </div>

          <button
            onClick={add}
            disabled={busy === 'new'}
            style={{
              marginTop: 12, padding: '11px 20px', borderRadius: 10, border: 'none',
              cursor: busy === 'new' ? 'default' : 'pointer', fontSize: 12.5, fontWeight: 700,
              color: T.white, background: T.teal, fontFamily: T.font, opacity: busy === 'new' ? 0.6 : 1,
            }}
          >
            {busy === 'new' ? 'Saving…' : 'Save plan'}
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ ...card, textAlign: 'center', fontSize: 12, color: T.textSec }}>Loading your plans…</div>
      ) : plans.length === 0 ? (
        <div style={{ ...card, textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🎯</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>No plans yet</div>
          <div style={{ fontSize: 11.5, color: T.textSec, marginTop: 6, lineHeight: 1.6 }}>
            A goal you set here stays with your record, so a doctor you share it with sees what you are working on.
          </div>
        </div>
      ) : (
        plans.map((p) => (
          <div key={p.groupId} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text }}>
                  {p.goalIcon && p.goalIcon.length <= 4 ? `${p.goalIcon} ` : ''}{p.goalLabel}
                </div>
                <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 2 }}>
                  {[
                    p.createdAt ? `Started ${fmtDate(p.createdAt)}` : null,
                    p.endDate ? `Review by ${fmtDate(p.endDate)}` : null,
                  ].filter(Boolean).join(' · ')}
                </div>
              </div>
              <button
                onClick={() => remove(p)}
                disabled={busy === p.groupId}
                style={{
                  padding: '7px 13px', borderRadius: 10, border: `1px solid ${T.danger}40`,
                  background: T.white, cursor: 'pointer', fontSize: 11, fontWeight: 600,
                  color: T.danger, fontFamily: T.font, whiteSpace: 'nowrap',
                }}
              >
                {busy === p.groupId ? 'Removing…' : 'Remove'}
              </button>
            </div>

            {p.summary && (
              <div style={{ fontSize: 12, color: T.text, marginTop: 10, lineHeight: 1.6 }}>{p.summary}</div>
            )}

            {/* What the plan actually asks of you. These are real reminders
                tied to the plan by `groupId` — the page showed only the goal,
                so a nine-item routine was invisible here. */}
            {(() => {
              const rows = itemsFor(p);
              if (!rows.length) {
                return (
                  <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 10, lineHeight: 1.5 }}>
                    Nothing is being tracked for this plan yet.
                  </div>
                );
              }
              return (
                <div style={{ marginTop: 12, borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: T.textSec, marginBottom: 6 }}>
                    {rows.length} tracked {rows.length === 1 ? 'item' : 'items'}
                  </div>
                  {rows.map((m) => (
                    <div key={m.id} style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '3px 0' }}>
                      <span style={{ fontSize: 10.5, color: T.textTer, minWidth: 42, fontVariantNumeric: 'tabular-nums' }}>
                        {m.times?.[0] ?? ''}
                      </span>
                      <span style={{ fontSize: 11.5, color: T.text, flex: 1, minWidth: 0 }}>{m.medicineName}</span>
                      {/* A diet goal is not a dose; the kind says which. */}
                      {m.kind && m.kind !== 'MEDICINE' && (
                        <span style={{
                          fontSize: 9, fontWeight: 700, borderRadius: 20, padding: '1px 7px',
                          color: m.kind === 'DIET' ? '#4D7C0F' : '#4338CA',
                          background: m.kind === 'DIET' ? '#F7FEE7' : '#EEF2FF',
                        }}>
                          {m.kind === 'DIET' ? 'Diet' : 'Routine'}
                        </span>
                      )}
                      {m.status !== 'ACTIVE' && (
                        <span style={{ fontSize: 9, color: T.textTer }}>{m.status.toLowerCase()}</span>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        ))
      )}
    </div>
  );
}
